import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PlusOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { Collapse, Form, Popconfirm, Row, Space } from 'antd';
import { BaseButtonsForm } from '@app/components/common/forms/BaseButtonsForm/BaseButtonsForm';
import { BaseInput } from '@app/components/common/inputs/BaseInput/BaseInput';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { BaseSelect, Option } from '@app/components/common/selects/BaseSelect/BaseSelect';
import { BaseSwitch } from '@app/components/common/BaseSwitch/BaseSwitch';
import { BaseRow } from '@app/components/common/BaseRow/BaseRow';
import { BaseCol } from '@app/components/common/BaseCol/BaseCol';
import { notificationController } from '@app/controllers/notificationController';
import { useAppDispatch } from '@app/hooks/reduxHooks';
import { createProductAction, getAllCategoriesAction, getAllVariantAction } from '@app/store/slices/userSlice';
import { BaseUpload } from '@app/components/common/BaseUpload/BaseUpload';
import config from '@app/config';
import { useNavigate } from 'react-router-dom';

const { Panel } = Collapse;

const formItemLayout = {
  labelCol: { span: 24 },
  wrapperCol: { span: 24 },
};

export const ValidationFormProductAdd: React.FC<any> = (props) => {
  const formRef = useRef<any>();
  const [fileId, setFileId] = useState(null);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [categories, setAllCategories] = useState([]);
  const [variants, setAllVariants] = useState([]);
  const [isVariantEnabled, setVariantEnabled] = useState(false);
  const [variantDetails, setVariantDetails] = useState([]); // Track variant details dynamically
  const [isLoading, setLoading] = useState(false);
  let navigate = useNavigate();
  useEffect(() => {
    if (!isVariantEnabled) setVariantDetails([]);
  }, [isVariantEnabled]);

  useEffect(() => {
    // Fetch categories and variants
    dispatch(getAllCategoriesAction())
      .unwrap()
      .then((resp) => {
        if (resp) setAllCategories(resp.data);
      })
      .catch((err: any) => notificationController.error({ message: err.message }));

    dispatch(getAllVariantAction())
      .unwrap()
      .then((resp) => {
        if (resp) setAllVariants(resp.data);
      })
      .catch((err: any) => notificationController.error({ message: err.message }));
  }, [dispatch]);

  const handleAddVariantOption = (variantId: string, selectedOptions: any[], name: string) => {
    if (variantDetails && variantDetails.length >= 1) {
      notificationController.warning({ message: "Only one variant allowed per product" });
      return;
    }

    const newOptions = selectedOptions.map((option) => ({
      quantity: option.split(' ')[0],
      unit: option.split(' ')[1],
      sale_price: '',
      regular_price: '',
      quantityAvailable: '',
    }));

    setVariantDetails((prev) => [
      ...prev,
      { variant_id: variantId, options: newOptions, name: name },
    ]);
  };

  const handleRemoveOption = (variantIndex: number, optionIndex: number) => {
    const updatedVariants = [...variantDetails];
    updatedVariants[variantIndex].options = updatedVariants[variantIndex].options.filter(
      (_, idx) => idx !== optionIndex
    );

    // Remove the variant completely if no options remain
    if (updatedVariants[variantIndex].options.length === 0) {
      updatedVariants.splice(variantIndex, 1);
    }

    setVariantDetails(updatedVariants);
  };

  const normFile = (e: any) => {
    if (e.file.status == 'done') {
      setFileId(e.file?.response?.file?._id);
    }
    if (Array.isArray(e.fileList)) {
      return e.fileList;
    }

    return e && e.fileList;
  };

  const beforeUpload = (file: any, fileList: any[]) => {
    const isJPG =
      file.type === "image/jpeg" ||
      file.type === "image/jpg" ||
      file.type === "image/png";
    if (!isJPG) {
      notificationController.warning({ message: "only .jpg, .jpeg, .png file types are allowed" });
      return Upload.LIST_IGNORE;
    }

    if (file.size > 5e+6) {
      notificationController.warning({ message: `Max Size Supported is 5 MB` });
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const handleRemoveVariant = () => {
    setVariantDetails([]);
    notificationController.info({ message: "Variant removed successfully" });
  };

  const handleOptionFieldChange = (variantIndex: number, optionIndex: number, field: string, value: string) => {
    const updatedVariants = [...variantDetails];
    updatedVariants[variantIndex].options[optionIndex][field] = value;

    // Validate sale price and regular price
    if (field === 'sale_price' || field === 'regular_price') {
      const salePrice = updatedVariants[variantIndex].options[optionIndex].sale_price;
      const regularPrice = updatedVariants[variantIndex].options[optionIndex].regular_price;

      if (parseFloat(salePrice) > parseFloat(regularPrice)) {
        notificationController.warning({ message: "Sale price cannot be greater than regular price" });
        return;
      }
    }

    setVariantDetails(updatedVariants);
  };

  const onFinish = async (values: any) => {
    if (isVariantEnabled && variantDetails.length === 0) {
      notificationController.warning({ message: 'You enabled variants but did not choose any' });
      return;
    }

    // Validate sale price and regular price for non-variant products
    if (!isVariantEnabled && parseFloat(values.sale_price) > parseFloat(values.regular_price)) {
      notificationController.warning({ message: "Sale price cannot be greater than regular price" });
      return;
    }

    setLoading(true);
    values.variants = isVariantEnabled ? variantDetails : []; // Attach variant details to the payload
    values.photo = fileId;
    values.isVariantEnabled = isVariantEnabled;
    if (!values.isActive) values.isActive = false;
    dispatch(createProductAction(values))
      .unwrap()
      .then((resp: any) => {
        setLoading(false);
        if (resp._id) {
          notificationController.success({ message: t('common.success') });
          navigate(-1);
        } else {
          notificationController.error({ message: 'Failed to save product' });
        }
      })
      .catch((err: any) => {
        setLoading(false);
        notificationController.error({ message: err.message });
      });
  };

  return (
    <Form {...formItemLayout} ref={formRef} name="addProductForm" onFinish={onFinish}>
      <BaseRow gutter={[10, 10]}>
        <BaseCol xs={24} sm={12}>
          <BaseButtonsForm.Item
            name="name"
            label={"Name"}
            rules={[{ required: true, message: "Name is required" }]}
          >
            <BaseInput />
          </BaseButtonsForm.Item>
        </BaseCol>

        {!isVariantEnabled && (
          <>
            <BaseCol xs={24} sm={12}>
              <BaseButtonsForm.Item
                name="regular_price"
                label={"Regular Price"}
                rules={[{ required: true, message: "Regular Price is required" }]}
              >
                <BaseInput type='number' min={1} />
              </BaseButtonsForm.Item>
            </BaseCol>
            <BaseCol xs={24} sm={12}>
              <BaseButtonsForm.Item
                name="sale_price"
                label={"Sale Price"}
                rules={[
                  { required: true, message: "Sale Price is required" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (parseFloat(value) > parseFloat(getFieldValue('regular_price')) && getFieldValue('regular_price')) {
                        return Promise.reject(new Error('Sale price cannot be greater than regular price'));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <BaseInput type='number' min={1} />
              </BaseButtonsForm.Item>
            </BaseCol>
          </>
        )}

        <BaseCol xs={24} sm={12}>
          <BaseButtonsForm.Item
            name="category"
            label={"Category"}
            rules={[{ required: true, message: "Category is required" }]}
          >
            <BaseSelect placeholder={"Please select a category"}>
              {categories && Array.isArray(categories) && categories.filter((x) => x.isActive).map((item: any) => (
                <Option value={item?._id}>{item?.name}</Option>
              ))}
            </BaseSelect>
          </BaseButtonsForm.Item>
        </BaseCol>

        <BaseCol xs={24} sm={12} md={8}>
          <BaseButtonsForm.Item
            name="photo"
            label={"Upload a photo"}
            valuePropName="fileList"
            rules={[
              {
                required: true,
                message: "Please select a photo"
              }
            ]}
            getValueFromEvent={normFile}
          >
            <BaseUpload beforeUpload={beforeUpload} name="file" action={config.API1Url + '/api/files/upload'} listType="picture" multiple={false} maxCount={1}>
              <BaseButton type="default" icon={<UploadOutlined />}>
                {t('forms.validationFormLabels.clickToUpload')}
              </BaseButton>
            </BaseUpload>
          </BaseButtonsForm.Item>
        </BaseCol>

        {!isVariantEnabled && (
          <>
            <BaseCol xs={24} sm={12}>
              <BaseButtonsForm.Item
                name="quantity"
                label={"Quantity (Size)"}
                rules={[{ required: true, message: "Quantity is required" }]}
              >
                <BaseInput placeholder='litre | mg | small-medium-large' type='number' />
              </BaseButtonsForm.Item>
            </BaseCol>

            <BaseCol xs={24} sm={12}>
              <BaseButtonsForm.Item
                name="unit"
                label={"unit"}
                rules={[{ required: true, message: "Unit is required" }]}
              >
                <BaseInput placeholder='litre | mg' />
              </BaseButtonsForm.Item>
            </BaseCol>

            <BaseCol xs={24} sm={12}>
              <BaseButtonsForm.Item
                name="quantityAvailable"
                label={"Quantity Available"}
                rules={[{ required: true, message: "Quantity Available is required" }]}
              >
                <BaseInput type='number' min={1} />
              </BaseButtonsForm.Item>
            </BaseCol>

            <BaseCol xs={24} sm={12}>
              <BaseButtonsForm.Item
                name="calories"
                label={"Calories"}
                rules={[{ required: true, message: "Calories is required" }]}
              >
                <BaseInput type='number' min={0} />
              </BaseButtonsForm.Item>
            </BaseCol>

            <BaseCol xs={24} sm={12}>
              <BaseButtonsForm.Item
                name="protein"
                label={"Protein"}
                rules={[{ required: true, message: "Protein is required" }]}
              >
                <BaseInput type='number' min={0} />
              </BaseButtonsForm.Item>
            </BaseCol>
          </>
        )}

        <BaseCol xs={24} sm={12}>
          <BaseButtonsForm.Item
            name="description"
            label={"Description"}
            rules={[{ required: true, message: "Description is required" }]}
          >
            <BaseInput.TextArea rows={5} />
          </BaseButtonsForm.Item>
        </BaseCol>

        {!props.outer && (
          <BaseCol xs={24} sm={12} md={8}>
            <BaseButtonsForm.Item name="isActive" label="Status" valuePropName="checked">
              <BaseSwitch />
            </BaseButtonsForm.Item>
          </BaseCol>
        )}
      </BaseRow>

      <BaseRow gutter={[10, 10]}>
        <BaseCol xs={24}>
          <BaseButtonsForm.Item name="enableVariants" valuePropName="checked" label="Enable Variants">
            <BaseSwitch onChange={(checked) => setVariantEnabled(checked)} />
          </BaseButtonsForm.Item>
        </BaseCol>
      </BaseRow>

      {isVariantEnabled && (
        <BaseRow gutter={[10, 10]}>
          <BaseCol xs={24}>
            <BaseSelect
              allowClear
              placeholder="Select Variant"
              onChange={(variantId) => {
                if (!variantId) return;
                const selectedVariant = variants.find((variant) => variant._id === variantId);
                const options = selectedVariant.values.map((value: any) => `${value.quantity} ${value.unit}`);
                handleAddVariantOption(variantId, options, selectedVariant.name);
              }}
            >
              {variants.map((variant: any) => (
                <Option key={variant?._id} value={variant?._id}>
                  {variant?.name}
                </Option>
              ))}
            </BaseSelect>
          </BaseCol>
        </BaseRow>
      )}

      {isVariantEnabled && variantDetails.map((variant, variantIndex) => (
        <>
          <Row gutter={[10, 10]}>
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', margin: '10px 0px' }}>
              <span>{variant.name}</span>
              <BaseButton type="primary" onClick={handleRemoveVariant} danger icon={<DeleteOutlined />}>
                Remove
              </BaseButton>
            </div>
          </Row>


          <Collapse key={variant.variant_id} activeKey={variant?.options.map((_, index) => index)} >
            {variant.options.map((option, optionIndex) => (
              <Panel key={optionIndex} header={`${option.quantity} ${option.unit}`}>
                <BaseRow gutter={[10, 10]}>
                  <BaseCol xs={24} sm={8}>
                    <Form.Item
                      label="Regular Price"
                      name={`regular_price_${variantIndex}_${optionIndex}`}
                      rules={[{ required: true, message: 'Regular Price is required' }]}
                    >
                      <BaseInput
                        placeholder="Regular Price"
                        value={option.regular_price}
                        onChange={(e) =>
                          handleOptionFieldChange(variantIndex, optionIndex, 'regular_price', e.target.value)
                        }
                      />
                    </Form.Item>
                  </BaseCol>

                  <BaseCol xs={24} sm={8}>
                    <Form.Item
                      label="Sale Price"
                      name={`sale_price_${variantIndex}_${optionIndex}`}
                      rules={[{ required: true, message: 'Sale Price is required' }]}
                    >
                      <BaseInput
                        placeholder="Sale Price"
                        value={option.sale_price}
                        onChange={(e) =>
                          handleOptionFieldChange(variantIndex, optionIndex, 'sale_price', e.target.value)
                        }
                      />
                    </Form.Item>
                  </BaseCol>

                  <BaseCol xs={24} sm={8}>
                    <Form.Item
                      label="Quantity Available"
                      name={`quantityAvailable_${variantIndex}_${optionIndex}`}
                      rules={[{ required: true, message: 'Quantity is required' }]}
                    >
                      <BaseInput
                        placeholder="Quantity Available"
                        value={option.quantityAvailable}
                        onChange={(e) =>
                          handleOptionFieldChange(variantIndex, optionIndex, 'quantityAvailable', e.target.value)
                        }
                      />
                    </Form.Item>
                  </BaseCol>

                  <BaseCol xs={24} sm={8}>
                    <Form.Item
                      label="Calories"
                      name={`calories_${variantIndex}_${optionIndex}`}
                      rules={[{ required: true, message: 'Calories are required' }]}
                    >
                      <BaseInput
                        placeholder="Calories"
                        value={option.calories}
                        onChange={(e) =>
                          handleOptionFieldChange(variantIndex, optionIndex, 'calories', e.target.value)
                        }
                      />
                    </Form.Item>
                  </BaseCol>

                  <BaseCol xs={24} sm={8}>
                    <Form.Item
                      label="Protein"
                      name={`protein_${variantIndex}_${optionIndex}`}
                      rules={[{ required: true, message: 'Protein is required' }]}
                    >
                      <BaseInput
                        placeholder="Protein"
                        value={option.protein}
                        onChange={(e) =>
                          handleOptionFieldChange(variantIndex, optionIndex, 'protein', e.target.value)
                        }
                      />
                    </Form.Item>
                  </BaseCol>

                  <BaseCol xs={24}>
                    <Popconfirm
                      title="Are you sure you want to remove this option?"
                      onConfirm={() => handleRemoveOption(variantIndex, optionIndex)}
                      okText="Yes"
                      cancelText="No"
                    >
                      <BaseButton danger icon={<DeleteOutlined />}>
                        Remove
                      </BaseButton>
                    </Popconfirm>
                  </BaseCol>
                </BaseRow>
              </Panel>
            ))}
          </Collapse>

        </>
      ))}

      <BaseRow gutter={[10, 10]}>
        <BaseCol xs={24}>
          <BaseButtonsForm.Item>
            <BaseButton type="primary" htmlType="submit" loading={isLoading}>
              {t('common.submit')}
            </BaseButton>
          </BaseButtonsForm.Item>
        </BaseCol>
      </BaseRow>
    </Form>
  );
};